/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2020, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import axios from 'axios';
import _ from 'lodash';
import urlJoin from 'proper-url-join';

import { getAccessPrefixedUrl } from '../../../ia/util/access-control';
import { endpoints } from '../../../ia/util/config';
import validation from '../../../ia/util/validation';

const validateAll = (counterExample, counterExamples, skillId) => {
  const validationErrors = [];
  if (counterExample) {
    const counterExampleErrors = validation.validateExample(counterExample);
    if (counterExampleErrors) {
      validationErrors.push(counterExampleErrors);
    }
  }
  if (counterExamples) {
    const counterExamplesErrors =
      validation.validateCounterExamples(counterExamples);
    if (counterExamplesErrors) {
      validationErrors.push(counterExamplesErrors);
    }
  }
  if (skillId) {
    const skillErrors = validation.validateGuid(skillId);
    if (skillErrors) {
      validationErrors.push(skillErrors);
    }
  }
  if (validationErrors.length) {
    return Promise.reject({ data: { error: validationErrors } });
  }
  return null;
};

const skillEndpoint = (
  skill_id: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string
) =>
  urlJoin(
    endpoints.store,
    getAccessPrefixedUrl(
      useNewIAUpdatedAccess,
      assistantId,
      `skills/${skill_id}`
    )
  );

const counterExamplesEndpoint = (
  skill_id: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string
) =>
  `${skillEndpoint(
    skill_id,
    useNewIAUpdatedAccess,
    assistantId
  )}/workspace/counterexamples/`;

const counterExampleEndpoint = (
  counterExampleText,
  skill_id: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string
) =>
  counterExamplesEndpoint(skill_id, useNewIAUpdatedAccess, assistantId) +
  encodeURIComponent(counterExampleText);

/**
 * Create a counter example
 * @param {*} counterExample — The new counter example
 * @param {string} skillId — The target skill id
 * @returns {axios} An axios post call that returns the example array
 */
export const createCounterExample = (
  counterExample: any,
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string
) => {
  const errors = validateAll(counterExample, null, skillId);
  if (errors) {
    return errors;
  }
  const url = counterExamplesEndpoint(
    skillId,
    useNewIAUpdatedAccess,
    assistantId
  );
  const params = { text: counterExample.text };
  return axios
    .post(url, params)
    .then(response => {
      return response.data;
    })
    .catch(error => Promise.reject(_.get(error, 'response.data') || error));
};

/**
 * Delete a counter example
 * @param {string} counterExample — The target counter example
 * @param {string} skillId — The target skill id
 * @returns {axios} An axios post call that returns the example array
 */
export const deleteCounterExample = (
  counterExample: any,
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string
) => {
  const errors = validateAll(counterExample, null, skillId);
  if (errors) {
    return errors;
  }
  const url = counterExampleEndpoint(
    counterExample.text,
    skillId,
    useNewIAUpdatedAccess,
    assistantId
  );
  return axios
    .delete(url)
    .then(response => response.data)
    .catch(error => Promise.reject(_.get(error, 'response.data') || error));
};

/**
 * Create or update multiple counter examples
 * @param {array} counterExamples — Array of counter examples to be updated
 * @param {string} skillId — The target skill id
 * @returns {axios} An axios post call that returns the example array
 */
export const updateCounterExamples = (
  counterExamples,
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string
) => {
  const errors = validateAll(null, counterExamples, skillId);
  if (errors) {
    return errors;
  }
  const url = skillEndpoint(skillId, useNewIAUpdatedAccess, assistantId);
  const params = { workspace: { counterexamples: [...counterExamples] } };
  return axios
    .post(url, params)
    .then(response => response.data)
    .catch(error => Promise.reject(_.get(error, 'response.data') || error));
};

/**
 * List counter examples for a given skill
 * @param {string} skillId — The target skill id
 * @returns {axios} An axios post call that returns the example array
 */
export const listCounterExamples = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string
) => {
  const errors = validateAll(null, null, skillId);
  if (errors) {
    return errors;
  }
  const url = counterExamplesEndpoint(
    skillId,
    useNewIAUpdatedAccess,
    assistantId
  );
  return axios
    .get(url)
    .then(response => response.data)
    .catch(error => Promise.reject(_.get(error, 'response.data') || error));
};
